import React, { useState } from 'react';
import { motion } from 'framer-motion';
import EmigoInteractiveIcon from './EmigoInteractiveIcon';
import RibbonStreaks from './RibbonStreaks';
import FloatingBubbles from './FloatingBubbles';
const Hero = () => {
  const [hasBeenTapped, setHasBeenTapped] = useState(false);
  const [cutKey, setCutKey] = useState(0);
  return <section id="hero" className="relative overflow-hidden flex flex-col justify-center pt-6 pb-20 md:pt-10 md:pb-24" style={{
    backgroundColor: 'var(--cream)',
    minHeight: 'calc(100vh - 72px)'
  }} data-id="element-159">
      <RibbonStreaks cutKey={cutKey} data-id="element-160" />
      <FloatingBubbles data-id="element-161" />

      {/* Cream reading-area mask — protects icon/headline/subhead/CTA from
                        the ribbon. Stronger and wider than before so strands stay
                        atmospheric at the edges only. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[2]" style={{
      background: 'radial-gradient(ellipse 65% 55% at 50% 50%, rgba(251, 248, 244, 0.96) 0%, rgba(251, 248, 244, 0.85) 35%, rgba(251, 248, 244, 0.45) 65%, rgba(251, 248, 244, 0) 90%)'
    }} data-id="element-162" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 text-center -translate-y-4 md:-translate-y-6" data-id="element-163">
        {/* Eyebrow */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-6" data-id="element-164">
          <span className="inline-block text-xs font-medium tracking-[0.2em] uppercase" style={{
          color: 'var(--coral)',
          // Compensate for trailing letter-spacing so the visible glyphs
          // optically center under the icon (CSS letter-spacing applies
          // after every char, including the last).
          paddingLeft: '0.2em'
        }} data-id="element-165">
            Emotionally intelligent messaging
          </span>
        </motion.div>

        {/* Main headline — three clean lines */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-[40px] sm:text-5xl md:text-[60px] lg:text-[68px] font-normal leading-[0.98] tracking-tight mb-8 md:mb-10" style={{
        color: 'var(--navy)',
        fontFamily: "'DM Serif Display', serif"
      }} data-id="element-166">
          <span className="block" data-id="element-167">For the messages that</span>
          <span className="block" data-id="element-168">
            matter{' '}
            <em className="italic" style={{
            color: 'var(--coral)'
          }} data-id="element-169">
              too much
            </em>
          </span>
          <span className="block" data-id="element-170">to get wrong.</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto text-[17px] md:text-[19px] lg:text-[20px] leading-[1.45] mb-7 md:mb-8" style={{
        color: 'var(--muted)',
        maxWidth: '640px'
      }} data-id="element-171">
          Rewrite what you're about to send. Understand what they really mean.
          Reply with clarity when it matters most.
        </motion.p>

        {/* CTA cluster — soft mini-card */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto flex flex-col items-center justify-center gap-3 px-6 py-6 md:px-8 md:py-7" style={{
        maxWidth: '480px',
        backgroundColor: 'rgba(255, 252, 247, 0.7)',
        border: '1px solid var(--peach)',
        borderRadius: '24px',
        boxShadow: '0 1px 2px rgba(26, 31, 54, 0.04), 0 12px 32px -16px rgba(232, 123, 92, 0.18)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }} data-id="element-172">
          <div className="flex items-center justify-center gap-2 mb-1" data-id="element-173">
            <span className="inline-flex items-center justify-center" style={{
            transform: 'scale(0.5)',
            transformOrigin: 'center',
            margin: '-18px -10px'
          }} data-id="element-174">
              <EmigoInteractiveIcon onTap={() => setHasBeenTapped(true)} onPop={() => setCutKey(k => k + 1)} data-id="element-175" />
            </span>
            <p className="text-[18px] md:text-[20px] leading-[1.3] text-center" style={{
            color: 'var(--navy)',
            fontFamily: "'DM Serif Display', serif"
          }} data-id="element-176">
              One tap is all it takes.
            </p>
          </div>
          <p className="text-[14px] md:text-[15px] leading-[1.5] mb-2 text-center" style={{
          color: 'var(--muted)'
        }} data-id="element-177">
            Reframe, interpret, or create right where you text, without
            switching apps.
          </p>

          <motion.a whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} href="https://apps.apple.com/us/app/emigo-better-texting/id6748092950" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-178">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/55BSmF1FDX8kQyzwZAZdd8/download-on-the-app-store.svg" alt="Download on the App Store" className="h-[56px] md:h-[60px]" data-id="element-179" />
          </motion.a>
          <p className="text-xs italic" style={{
          color: 'var(--muted-warm)'
        }} data-id="element-180">
            Free. No sign-up. No personal info.
          </p>
        </motion.div>
      </div>
    </section>;
};
export default Hero;